/**
 * Select words inside any Study Buddy reply → a small "Save to notes" button
 * appears right above the selection.
 */
import { useEffect, useState } from 'react';
import { Loader2, NotebookPen } from 'lucide-react';
import { toast } from 'sonner';
import { announceNoteSaved, captureNote } from '@/lib/notes';

export function SelectionSaver({ containerRef, sessionId }) {
  const [pick, setPick] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const root = containerRef.current;
    if (!root || !sessionId) return undefined;
    const check = () => setTimeout(() => {
      const sel = window.getSelection();
      const text = sel?.toString().trim();
      const node = sel?.anchorNode?.parentElement;
      const bubble = node?.closest?.('[data-ai-message-id]');
      if (!text || text.length < 3 || !bubble || !root.contains(bubble)) { setPick(null); return; }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setPick({ text: text.slice(0, 1500), messageId: bubble.dataset.aiMessageId, x: rect.left + rect.width / 2, y: rect.top });
    }, 10);
    root.addEventListener('mouseup', check);
    root.addEventListener('touchend', check);
    const hide = () => setPick(null);
    root.addEventListener('scroll', hide, true);
    return () => {
      root.removeEventListener('mouseup', check);
      root.removeEventListener('touchend', check);
      root.removeEventListener('scroll', hide, true);
    };
  }, [containerRef, sessionId]);

  if (!pick) return null;

  const save = async () => {
    setSaving(true);
    try {
      const r = await captureNote({ session_id: sessionId, mode: 'selection', text: pick.text, message_id: pick.messageId });
      toast.success(`Saved under “${r.notes[0]?.heading}”`);
      announceNoteSaved(r.notes);
      window.getSelection()?.removeAllRanges();
      setPick(null);
    } catch (e) {
      toast.error(e?.message || 'Could not save that');
    } finally { setSaving(false); }
  };

  return (
    <button onMouseDown={e => e.preventDefault()} onClick={save} disabled={saving}
      data-testid="selection-save-note-btn"
      style={{ left: pick.x, top: Math.max(8, pick.y - 44) }}
      className="fixed z-50 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-xs font-heading shadow-lg transition-colors">
      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <NotebookPen className="w-3.5 h-3.5" />}
      Save to notes
    </button>
  );
}

export default SelectionSaver;
